import React from 'react';
import RouteWaypointLabel from '../route-waypoint-label';

const COINCIDENT_EPS = 1e-5;

type WaypointLike = {
  latitude?: string | number | null;
  longitude?: string | number | null;
  orderId?: string | number | null;
  type?: string | null;
  label?: string;
};

type RouteWaypointNumbersProps = {
  waypoints?: WaypointLike[] | null;
  takeoff?: google.maps.LatLngLiteral | null;
  destination?: google.maps.LatLngLiteral | null;
};

const toLatLng = (loc?: WaypointLike | null): google.maps.LatLngLiteral | null => {
  if (!loc) return null;
  const lat = Number(loc.latitude);
  const lng = Number(loc.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const coincides = (a: google.maps.LatLngLiteral, b?: google.maps.LatLngLiteral | null) =>
  !!b && Math.abs(a.lat - b.lat) < COINCIDENT_EPS && Math.abs(a.lng - b.lng) < COINCIDENT_EPS;

const isHome = (w: WaypointLike) => w.type === 'HOME' || w.label === 'H';

// Numbered purple labels for the intermediate route vertices. Skips the drone HOME point,
// the takeoff/destination pins (they get their own markers), and any duplicated coordinate
// (e.g. the return-to-takeoff vertex) so the sequence stays 1..N.
const RouteWaypointNumbers: React.FC<RouteWaypointNumbersProps> = ({
  waypoints = [],
  takeoff,
  destination,
}) => {
  const seen = new Set<string>();
  let counter = 0;
  const numbered = (waypoints ?? []).reduce<
    { position: google.maps.LatLngLiteral; waypointNumber: number }[]
  >((acc, wp) => {
    if (wp.orderId || isHome(wp)) return acc;
    const position = toLatLng(wp);
    if (!position) return acc;
    if (coincides(position, takeoff) || coincides(position, destination)) return acc;
    const key = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
    if (seen.has(key)) return acc;
    seen.add(key);
    counter += 1;
    acc.push({ position, waypointNumber: counter });
    return acc;
  }, []);

  return (
    <React.Fragment>
      {numbered.map(({ position, waypointNumber }) => (
        <RouteWaypointLabel
          key={`${position.lat},${position.lng}-${waypointNumber}`}
          position={position}
          waypointNumber={waypointNumber}
        />
      ))}
    </React.Fragment>
  );
};

export default RouteWaypointNumbers;
